/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  ExpandMore as ExpandIcon,
  Lock as AuthIcon,
  MedicalServices as M2Icon,
  PersonAdd as M1Icon,
  Policy as M3Icon,
} from '@mui/icons-material';
import { useState } from 'react';
import { PageShell } from '@/layout/PageShell';
import { useToast } from '@/components/Toaster';

// ─── types ────────────────────────────────────────────────────────

interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  title: string;
  description: string;
  headers?: Record<string, string>;
  request?: unknown;
  response?: unknown;
  notes?: string;
}

interface Section {
  id: string;
  label: string;
  description: string;
  endpoints: Endpoint[];
}

// ─── data ─────────────────────────────────────────────────────────

const BASE_URL = 'https://your-abdm-connector-host:8089';

const AUTH_HEADERS = {
  Authorization: 'Bearer {jwt_token}',
  'Content-Type': 'application/json',
  'X-Tenant-Id': '{your_tenant_id}',
};

const SECTIONS: Section[] = [
  {
    id: 'auth',
    label: 'Authentication',
    description: 'Obtain a JWT token before calling any other endpoint.',
    endpoints: [
      {
        method: 'POST',
        path: '/api/auth/login',
        title: 'Login & obtain JWT',
        description: 'Exchange credentials for a JWT token. Pass this token as Authorization: Bearer {token} on every subsequent request.',
        headers: { 'Content-Type': 'application/json' },
        request: {
          username: 'tenant_user@example.com',
          password: 'secret',
        },
        response: {
          token: 'eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ0ZW5hbnRfdXNlciIsInRlbmFudElkIjoiVEVOQU5UMSIsImlhdCI6MTcxMDAwMDAwMCwiZXhwIjoxNzEwMDg2NDAwfQ.SIGNATURE',
          tenantId: 'TENANT1',
          expiresIn: 86400,
        },
        notes: 'The token expires in 24 hours. Re-login before making production calls or store a refresh strategy.',
      },
    ],
  },
  {
    id: 'm1',
    label: 'M1 – Registration & Care Context',
    description: 'Register patients and link their care contexts to ABHA. These are the foundational steps before any health data can flow through ABDM.',
    endpoints: [
      {
        method: 'POST',
        path: '/api/abdm/patient/save',
        title: 'Register / update patient',
        description: 'Store patient demographics in the connector. If the patient already exists (same memberId), their profile is updated.',
        request: {
          tenantId: 'TENANT1',
          memberId: 'MBR001',
          name: 'Ravi Kumar',
          gender: 'male',
          birthDate: '1988-04-15',
          mobile: '9876543210',
          abhaAddress: 'ravi.kumar@abdm',
          abhaNumber: '91-1234-5678-9012',
        },
        response: {
          status: 'SUCCESS',
          memberId: 'MBR001',
          message: 'Patient registered successfully',
        },
      },
      {
        method: 'POST',
        path: '/api/abdm/link/carecontext',
        title: 'Link care context to ABHA',
        description: "Creates a linkage between a consultation/visit and the patient's ABHA profile. The patient receives an OTP on their ABHA app to confirm.",
        request: {
          tenantId: 'TENANT1',
          memberId: 'MBR001',
          abhaAddress: 'ravi.kumar@abdm',
          careContextReference: 'OPD-20260512-A1B2',
          display: 'OPD Visit – 12 May 2026',
          hipId: 'AYUSHSETU-HIP',
          hipName: 'AyushSetu Hospital',
        },
        response: {
          status: 'INITIATED',
          requestId: 'req-uuid-1234',
          message: 'Linking initiated; OTP sent to patient',
        },
        notes: 'The patient confirms via ABHA app. Listen to the /abdm/callback/link/on-confirm webhook to know when linking succeeds.',
      },
      {
        method: 'POST',
        path: '/api/abdm/v3/abha/enrollment/request/otp',
        title: 'Initiate ABHA creation – request OTP',
        description: "Starts the ABHA number enrollment process by sending an OTP to the patient's mobile. Use the returned txnId for the next step.",
        request: {
          tenantId: 'TENANT1',
          memberId: 'MBR001',
          mobile: '9876543210',
          loginHint: 'mobile',
          otpSystem: 'abdm',
        },
        response: {
          txnId: 'txn-uuid-abcd',
          message: 'OTP sent to 9876543210',
        },
      },
      {
        method: 'POST',
        path: '/api/abdm/v3/abha/enrollment/verify/otp',
        title: 'Verify OTP & complete ABHA enrollment',
        description: 'Verify the mobile OTP received in the previous step and complete ABHA number creation.',
        request: {
          tenantId: 'TENANT1',
          memberId: 'MBR001',
          txnId: 'txn-uuid-abcd',
          otp: '123456',
          name: 'Ravi Kumar',
          gender: 'M',
          yearOfBirth: '1988',
          monthOfBirth: '04',
          dayOfBirth: '15',
        },
        response: {
          abhaNumber: '91-1234-5678-9012',
          abhaAddress: 'ravi.kumar@abdm',
          healthId: 'ravi.kumar@abdm',
          name: 'Ravi Kumar',
          gender: 'M',
        },
      },
    ],
  },
  {
    id: 'm2',
    label: 'M2 – Push Health Records (HIP)',
    description: 'Push structured FHIR R4 bundles to ABDM so patients can see their health records in the ABHA PHR app. Supports 8 document types.',
    endpoints: [
      {
        method: 'POST',
        path: '/api/abdm/consultation/push',
        title: 'Push FHIR bundle (all 8 types)',
        description: 'The core M2 endpoint. Send a complete clinical event as a structured FHIR bundle. The connector enriches it into a valid FHIR R4 Bundle and delivers it to ABDM.',
        notes: 'Set fhirBundleType to one of: Prescription | OPConsultation | DiagnosticReport | HealthDocumentRecord | WellnessRecord | Immunization | DischargeSummary | Invoice',
        request: {
          tenantId: 'TENANT1',
          memberId: 'MBR001',
          consultationNumber: 'OPD-20260512-A1B2',
          status: 'CLOSED',
          appointmentDate: '2026-05-12T10:00:00.000Z',
          linkedToABDM: true,
          abhaAddress: 'ravi.kumar@abdm',
          abhaNumber: '91-1234-5678-9012',
          fhirBundleType: 'Prescription',
          clinical_data: {
            bundleType: 'PrescriptionRecord',
            careContextReference: 'OPD-20260512-A1B2',
            encounter: 'OPD-20260512-E001',
            patient: {
              name: 'Ravi Kumar',
              patientReference: 'ravi.kumar@abdm',
              gender: 'male',
              birthDate: '1988-04-15',
            },
            practitioners: [{ name: 'Dr. Priya Singh', practitionerId: 'DOC001' }],
            organisation: { facilityName: 'AyushSetu Hospital', facilityId: 'FAC001' },
            visitDate: '2026-05-12T10:00:00.000Z',
            encounterType: 'Outpatient',
            fhir: {
              resourceType: 'MedicationRequest',
              intent: 'order',
              status: 'active',
              priority: 'routine',
              authoredOn: '2026-05-12T10:00:00.000Z',
            },
            medications: [
              {
                medicineName: 'Paracetamol 500mg',
                dosagePattern: '1-0-1',
                timing: '1-1-D',
                route: 'Oral',
                method: 'Swallow',
                quantity: '1',
                intake: 'After Food',
                reason: 'Fever',
                additionalInstructions: 'Take with water',
              },
            ],
            documents: [],
          },
        },
        response: {
          status: 'SUCCESS',
          consultationNumber: 'OPD-20260512-A1B2',
          fhirBundleType: 'Prescription',
          message: 'FHIR bundle pushed to ABDM successfully',
          abdmRequestId: 'abdm-req-uuid-5678',
        },
      },
      {
        method: 'GET',
        path: '/api/abdm/consultation/status/{consultationNumber}',
        title: 'Check push status',
        description: 'Query the delivery status of a previously pushed FHIR bundle. Useful to confirm the record reached ABDM.',
        response: {
          consultationNumber: 'OPD-20260512-A1B2',
          fhirBundleType: 'Prescription',
          status: 'DELIVERED',
          linkedToABDM: true,
          lastUpdated: '2026-05-12T10:05:00.000Z',
        },
      },
      {
        method: 'POST',
        path: '/api/abdm/consultation/push',
        title: 'OPConsultation example payload',
        description: 'Full OPConsultation bundle with ICD-10 diagnosis, chief complaints, physical exams, allergies, medications, and follow-ups.',
        request: {
          tenantId: 'TENANT1',
          memberId: 'MBR001',
          consultationNumber: 'OPD-20260512-B2C3',
          status: 'CLOSED',
          appointmentDate: '2026-05-12T11:00:00.000Z',
          linkedToABDM: true,
          abhaAddress: 'ravi.kumar@abdm',
          abhaNumber: '91-1234-5678-9012',
          fhirBundleType: 'OPConsultation',
          clinical_data: {
            bundleType: 'OPConsultationRecord',
            careContextReference: 'OPD-20260512-B2C3',
            encounter: 'OPD-20260512-E002',
            patient: { name: 'Ravi Kumar', patientReference: 'ravi.kumar@abdm', gender: 'male', birthDate: '1988-04-15' },
            practitioners: [{ name: 'Dr. Priya Singh', practitionerId: 'DOC001' }],
            organisation: { facilityName: 'AyushSetu Hospital', facilityId: 'FAC001' },
            visitDate: '2026-05-12T11:00:00.000Z',
            encounterType: 'Outpatient',
            icd10Diagnosis: 'J06.9 Acute upper respiratory infection',
            chiefComplaints: [{ complaint: 'Sore throat' }, { complaint: 'Mild fever' }],
            physicalExaminations: [{ observation: 'Throat examination', result: 'Redness noted' }],
            allergies: ['Penicillin', 'Dust'],
            medicalHistories: [{ complaint: 'Hypertension' }],
            medications: [
              { medicine: 'Azithromycin 500mg', dosage: '1-0-0', timing: '1-1-D', route: 'Oral', method: 'Swallow', reason: 'Bacterial infection', additionalInstructions: '' },
            ],
            followups: [{ serviceType: 'General Practice', status: 'proposed', startDate: '2026-05-19T00:00:00.000Z', endDate: '', reason: 'Review in 1 week' }],
            procedures: [],
            documents: [],
          },
        },
        response: { status: 'SUCCESS', consultationNumber: 'OPD-20260512-B2C3', fhirBundleType: 'OPConsultation', message: 'FHIR bundle pushed to ABDM successfully' },
      },
    ],
  },
  {
    id: 'm3',
    label: 'M3 – Consent & Data Fetch (HIU)',
    description: 'Request patient consent to access historical health records from other HIPs. The patient approves via the ABHA app, and ABDM delivers the data.',
    endpoints: [
      {
        method: 'POST',
        path: '/api/abdm/m3/consent/request/v3',
        title: 'Raise consent request',
        description: 'Ask the patient to grant consent for accessing their health records. The patient receives a notification on their ABHA app to approve or deny.',
        request: {
          tenantId: 'TENANT1',
          memberId: 'MBR001',
          patientAbhaAddress: 'ravi.kumar@abdm',
          purpose: {
            code: 'CAREMGT',
            display: 'Care Management',
            system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
          },
          hiTypes: ['Prescription', 'OPConsultation', 'DiagnosticReport'],
          dateRange: {
            from: '2024-01-01T00:00:00.000Z',
            to: '2026-05-12T23:59:59.000Z',
          },
          dataEraseAt: '2026-08-12T00:00:00.000Z',
          hiu: { id: 'AYUSHSETU-HIU', name: 'AyushSetu Hospital' },
        },
        response: {
          status: 'INITIATED',
          consentRequestId: 'consent-req-uuid-9999',
          message: 'Consent request sent to patient',
        },
        notes: 'Listen to the webhook at /abdm/callback/consent/on-notify for status updates. The patient must approve within 24 hours.',
      },
      {
        method: 'GET',
        path: '/api/abdm/consent/list',
        title: 'List consent requests',
        description: 'Retrieve all consent requests for your tenant, with their current status (REQUESTED, GRANTED, DENIED, EXPIRED, REVOKED).',
        response: {
          content: [
            {
              consentRequestId: 'consent-req-uuid-9999',
              patientAbhaAddress: 'ravi.kumar@abdm',
              status: 'GRANTED',
              hiTypes: ['Prescription', 'OPConsultation'],
              createdAt: '2026-05-12T10:00:00.000Z',
            },
          ],
          totalElements: 1,
          totalPages: 1,
        },
      },
      {
        method: 'GET',
        path: '/api/abdm/consent/{consentRequestId}',
        title: 'Get consent details & artefacts',
        description: 'Retrieve consent artefacts (tokens) for an approved consent. These are needed to trigger health data fetch.',
        response: {
          consentRequestId: 'consent-req-uuid-9999',
          status: 'GRANTED',
          consentArtefacts: [
            { artefactId: 'artefact-uuid-1111', hipId: 'HIP001', hiTypes: ['Prescription'], createdAt: '2026-05-12T10:30:00.000Z' },
          ],
        },
      },
      {
        method: 'POST',
        path: '/api/abdm/m3/health-information/request',
        title: 'Fetch approved health data',
        description: 'Trigger health data fetch using consent artefact IDs. ABDM delivers the FHIR bundles to your callback URL asynchronously.',
        request: {
          tenantId: 'TENANT1',
          memberId: 'MBR001',
          consentRequestId: 'consent-req-uuid-9999',
          artefactId: 'artefact-uuid-1111',
          dataCallbackUrl: 'https://your-hmis.example.com/abdm/data-callback',
        },
        response: {
          status: 'INITIATED',
          transactionId: 'txn-health-info-2222',
          message: 'Health information request sent to HIP',
        },
        notes: 'ABDM will call your dataCallbackUrl with the FHIR bundles. Process these asynchronously.',
      },
    ],
  },
];

// ─── component ────────────────────────────────────────────────────

export function ApiReferencePage() {
  const theme = useTheme();
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [expanded, setExpanded] = useState<string | false>(false);

  const section = SECTIONS[tab] ?? SECTIONS[0]!;

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!'));
  };

  const buildCurl = (ep: Endpoint): string => {
    const hdrs = Object.entries({ ...AUTH_HEADERS, ...(ep.headers ?? {}) })
      .map(([k, v]) => `  -H '${k}: ${v}'`)
      .join(' \\\n');
    const body = ep.request
      ? ` \\\n  -d '${JSON.stringify(ep.request, null, 2)}'`
      : '';
    return `curl -X ${ep.method} '${BASE_URL}${ep.path}' \\\n${hdrs}${body}`;
  };

  const methodColor = (m: string) => {
    if (m === 'GET') return theme.palette.success.main;
    if (m === 'POST') return theme.palette.info.main;
    if (m === 'PUT') return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <PageShell
      kicker="TENANT · DEVELOPER DOCS"
      title="API Reference"
      subtitle="Minimal integration guide — connect your HMIS or clinic system to ABDM via M1 / M2 / M3"
    >
      {/* Base URL + Auth notice */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography variant="body2" fontWeight={600}>Base URL:</Typography>
          <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 13 }}>{BASE_URL}</Box>
          <Typography variant="body2" color="text.secondary">All endpoints require <code>Authorization: Bearer {'{jwt_token}'}</code> and <code>X-Tenant-Id: {'{tenant_id}'}</code></Typography>
        </Stack>
      </Alert>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setExpanded(false); }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<AuthIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Auth" />
        <Tab icon={<M1Icon sx={{ fontSize: 16 }} />} iconPosition="start" label="M1 Registration" />
        <Tab icon={<M2Icon sx={{ fontSize: 16 }} />} iconPosition="start" label="M2 Push Records" />
        <Tab icon={<M3Icon sx={{ fontSize: 16 }} />} iconPosition="start" label="M3 Consent & Fetch" />
      </Tabs>

      {/* Section description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{section.description}</Typography>

      {/* Endpoint accordions */}
      {section.endpoints.map((ep, idx) => {
        const key = `${section.id}-${idx}`;
        return (
          <Accordion
            key={key}
            expanded={expanded === key}
            onChange={(_, open) => setExpanded(open ? key : false)}
            disableGutters
            sx={{
              mb: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: `${theme.radius?.md ?? 8}px !important`,
              '&:before': { display: 'none' },
              boxShadow: 'none',
            }}
          >
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ px: 2, py: 1 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, mr: 1 }}>
                <Chip
                  label={ep.method}
                  size="small"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: 11,
                    minWidth: 48,
                    bgcolor: methodColor(ep.method) + '22',
                    color: methodColor(ep.method),
                    border: `1px solid ${methodColor(ep.method)}55`,
                  }}
                />
                <Box component="code" sx={{ fontSize: 13, color: 'text.secondary', fontFamily: 'monospace' }}>
                  {ep.path}
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ ml: 1 }}>{ep.title}</Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{ep.description}</Typography>

              {ep.notes && (
                <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
                  <Typography variant="caption">{ep.notes}</Typography>
                </Alert>
              )}

              <Stack spacing={2}>
                {/* Headers */}
                <Box>
                  <SectionLabel>Required Headers</SectionLabel>
                  <CodeBlock
                    code={Object.entries({ ...AUTH_HEADERS, ...(ep.headers ?? {}) })
                      .map(([k, v]) => `${k}: ${v}`)
                      .join('\n')}
                    onCopy={copyText}
                  />
                </Box>

                {/* Request body */}
                {!!ep.request && (
                  <Box>
                    <SectionLabel>Request Body (JSON)</SectionLabel>
                    <CodeBlock code={JSON.stringify(ep.request, null, 2)} onCopy={copyText} />
                  </Box>
                )}

                {/* Response */}
                {!!ep.response && (
                  <Box>
                    <SectionLabel>Sample Response</SectionLabel>
                    <CodeBlock code={JSON.stringify(ep.response, null, 2)} onCopy={copyText} />
                  </Box>
                )}

                {/* cURL */}
                <Box>
                  <SectionLabel>cURL Example</SectionLabel>
                  <CodeBlock code={buildCurl(ep)} onCopy={copyText} lang="bash" />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Flow diagram note */}
      {tab > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="caption">
            {tab === 1 && 'M1 flow: Register patient → Create ABHA → Link care context → patient confirms OTP in ABHA app'}
            {tab === 2 && 'M2 flow: Patient visits → HMIS calls Push endpoint → Connector builds FHIR bundle → Pushes to ABDM → Patient sees record in ABHA app'}
            {tab === 3 && 'M3 flow: HMIS raises consent request → Patient approves in ABHA app → Consent webhook fires → HMIS fetches health data → ABDM delivers FHIR bundles'}
          </Typography>
        </Alert>
      )}
    </PageShell>
  );
}

// ─── sub-components ───────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 0.5 }}>
      {children}
    </Typography>
  );
}

function CodeBlock({ code, onCopy, lang = 'json' }: { code: string; onCopy: (s: string) => void; lang?: string }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 1.5,
        fontFamily: 'monospace',
        fontSize: 12,
        overflowX: 'auto',
        whiteSpace: 'pre',
        color: theme.palette.mode === 'dark' ? 'grey.100' : 'grey.900',
      }}
    >
      <Tooltip title="Copy">
        <IconButton
          size="small"
          onClick={() => onCopy(code)}
          sx={{ position: 'absolute', top: 6, right: 6, opacity: 0.6, '&:hover': { opacity: 1 } }}
          aria-label={`copy ${lang} snippet`}
        >
          <CopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      {code}
    </Box>
  );
}
